import { Injectable } from '@angular/core';
import { AppConfigService } from '@markant-platform/shared/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { HttpClient } from '@angular/common/http';
import { combineLatest, firstValueFrom, forkJoin, map, Observable, of, Subject, switchMap } from 'rxjs';
import {
  CartridgeProjection,
  PlatformCartridgeWidget,
  PlatformWidget,
  Widget,
  WidgetIdentity,
  WidgetPosition,
  WidgetsGroup,
} from '../models/dashboard.models';
import { TranslocoService } from '@jsverse/transloco';
import { StaticRole } from '@markant-platform/shared/user/model';
import { BookingService } from '@markant-platform/cartridge/data-access';
import { isBulkPrice } from '@markant-platform/shared/util-helpers';

import { DashboardConfig } from '../dashboard-widgets/configuration/dashboard-widgets.config';
import { ActiveBookingStates, BookingData, Price } from '@markant-platform/cartridge/model';
import { QualificationLevel } from 'data-model';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private userName?: string;
  private readonly userCartridgeServiceApiUrl = AppConfigService.appConfiguration.apiUrl['userCartridgeService'];

  private platformWidgets: {
    STATIC: PlatformWidget[];
    PLATFORM: PlatformWidget[];
    PLATFORM_CARTRIDGE: PlatformCartridgeWidget[];
  } = DashboardConfig.PLATFORM_WIDGETS;

  private widgetRemovalTriggerSubject: Subject<WidgetIdentity> = new Subject<WidgetIdentity>();
  widgetRemovalTrigger$: Observable<WidgetIdentity> = this.widgetRemovalTriggerSubject.asObservable();

  constructor(
    private authenticatedUserService: AuthenticatedUserService,
    private bookingService: BookingService,
    private translocoService: TranslocoService,
    private http: HttpClient,
    private organizationService: OrganizationService
  ) {}

  getUserName$(): Observable<string> {
    if (this.userName) {
      return of(this.userName);
    }

    return this.authenticatedUserService
      .getUser()
      .pipe(map(user => (this.userName = `${user.firstname} ${user.lastname}`)));
  }

  getUserRoles$(): Observable<string[]> {
    return this.authenticatedUserService.getUserRoles().pipe(map(roles => roles.map(({ name }) => name)));
  }

  getVisibleWidgets$(): Observable<Widget[]> {
    return this.http.get<Widget[]>(`${this.userCartridgeServiceApiUrl}/visible-widgets`).pipe(
      map(widgets =>
        widgets.filter(widget => {
          return this.filterInvalidPlatformWidgets(widget);
        })
      ),
      switchMap(async visibleWidgets => {
        return await this.setVisibleWidgets(visibleWidgets);
      })
    );
  }

  getOrganizationQualificationLevel$(): Observable<QualificationLevel> {
    return this.organizationService
      .getCurrentOrganization()
      .pipe(map(org => org?.qualificationLevel as QualificationLevel));
  }

  getAvailableWidgetsGrouped$(roles: string[]): Observable<WidgetsGroup[]> {
    const filteredPlatformWidgets = this.platformWidgets.PLATFORM.filter(widget => !widget.disabled).filter(
      widget =>
        !widget.isNotRemovable &&
        !widget.isNotAbleToAddAgain &&
        widget.allowedRoles.some(role => roles.some(userRole => userRole === role))
    );

    return this.translocoService.langChanges$.pipe(
      switchMap(() => {
        const platformWidgetsGroup = {
          groupName: this.translocoService.translate('dashboard.selection.platformGroup'),
          groupId: 'platform',
          variantId: null,
          widgets: filteredPlatformWidgets.map(widget => ({
            ...widget,
            ...(!widget.hideWidgetTitle && { displayName: widget.displayName }),
          })),
        };

        // ~~Leistung and Platform-admins can only get platform widgets~~
        // changed by AnFu, see: DMP-8385: PFAs, LAs belonging to an ORG can also have cartridge rights from their organization
        // @zeigert there seems to have been some misunderstanding? this code is very recent, but produces this "bug?"
        // -> replaced by code below to always try to get cartridges, iff user (even admins) has an organization
        // if(roles.includes(StaticRole.LEISTUNG_ADMIN) || roles.includes(StaticRole.PLATFORM_ADMIN)) {
        //   return of(filteredPlatformWidgets.length ? [platformWidgetsGroup] : []);
        // }

        return this.authenticatedUserService.getOrganizationId().pipe(
          switchMap(() =>
            combineLatest([
              this.bookingService.getBookingsForUser(ActiveBookingStates).pipe(map(({ result }) => result)),
              this.http.get<Pick<CartridgeProjection, 'name' | 'variantId' | 'widgets'>[]>(
                `${this.userCartridgeServiceApiUrl}`
              ),
            ])
          ),
          map(([bookings, userCartridges]) => {
            const cartridgeWidgetGroups: WidgetsGroup[] = userCartridges.map(({ name, variantId, widgets }) => ({
              groupName: name,
              groupId: variantId,
              variantId,
              widgets: widgets as Widget[],
            }));
            const platformCartridgeWidgetGroup: WidgetsGroup[] = bookings.map(
              ({ cartridgeName, cartridgeVariantId, price }) => ({
                groupName: cartridgeName,
                groupId: cartridgeVariantId,
                variantId: null,
                widgets: [...this.buildUsageReportWidget(cartridgeVariantId, price)]
                  .filter(widget => !widget.disabled)
                  .filter(
                    widget =>
                      !widget.isNotRemovable &&
                      !widget.isNotAbleToAddAgain &&
                      widget.allowedRoles.some(role => roles.some(userRole => userRole === role))
                  ),
              })
            );

            const widgetsGroups = cartridgeWidgetGroups
              .concat(platformCartridgeWidgetGroup)
              .filter(({ widgets }) => !!widgets.length)
              .reduce((agg, widgetGroup) => {
                const existingWidgets = agg.get(widgetGroup.groupId)?.widgets ?? [];
                agg.set(widgetGroup.groupId, { ...widgetGroup, widgets: [...existingWidgets, ...widgetGroup.widgets] });

                return agg;
              }, new Map<string, WidgetsGroup>());

            const widgetGroups = [...widgetsGroups.values()];
            return filteredPlatformWidgets.length ? [platformWidgetsGroup, ...widgetGroups] : widgetGroups;
          })
        );
      })
    );
  }

  hasAvailableAndAddableWidgets$(
    roles: string[],
    qualificationLevel: QualificationLevel
  ): Observable<{ hasAvailableWidgets: boolean; hasAddableWidgets: boolean }> {
    const availablePlatformWidgets = this.platformWidgets.PLATFORM.concat(
      this.platformWidgets.PLATFORM_CARTRIDGE as unknown as PlatformWidget[]
    )
      .filter(widget => !widget.disabled)
      .filter(
        widget =>
          !widget.allowedQualificationLevels ||
          (qualificationLevel && widget.allowedQualificationLevels.includes(qualificationLevel))
      )
      .filter(widget => widget.allowedRoles.some(role => roles.some(userRole => userRole === role)));
    const hasAddablePlatformWidgets = availablePlatformWidgets.some(widget => !widget.isNotAbleToAddAgain);

    if (roles.includes(StaticRole.LEISTUNG_ADMIN) || roles.includes(StaticRole.PLATFORM_ADMIN)) {
      return of({
        hasAvailableWidgets: !!availablePlatformWidgets.length,
        hasAddableWidgets: hasAddablePlatformWidgets,
      });
    }

    return this.http
      .get<CartridgeProjection[]>(this.userCartridgeServiceApiUrl)
      .pipe(
        map(cartridges =>
          cartridges.some(cartridge => cartridge.widgets.length > 0)
            ? { hasAvailableWidgets: true, hasAddableWidgets: true }
            : { hasAvailableWidgets: !!availablePlatformWidgets.length, hasAddableWidgets: hasAddablePlatformWidgets }
        )
      );
  }

  updateVisibleWidgets(updatedVisibleWidgets: WidgetPosition[]): Observable<Widget[]> {
    return this.http
      .put<Widget[]>(
        `${this.userCartridgeServiceApiUrl}/visible-widgets`,
        updatedVisibleWidgets.filter(widget => widget.widgetId !== 'add-widget')
      )
      .pipe(switchMap(async visibleWidgets => await this.setVisibleWidgets(visibleWidgets)));
  }

  getUpdatedVisibleWidgets(roles: string[]): Observable<Widget[]> {
    return forkJoin([this.getVisibleWidgets$(), this.organizationService.fetchOrganization()]).pipe(
      switchMap(async ([visibleWidgets, organization]) => {
        const nonPlatformWidgets = visibleWidgets.filter(
          widget => widget.widgetType !== 'PLATFORM' && widget.widgetType !== 'PLATFORM_CARTRIDGE'
        );
        const platformWidgets = visibleWidgets
          .filter(widget => widget.widgetType === 'PLATFORM' || widget.widgetType === 'PLATFORM_CARTRIDGE')
          .map(widget => widget as PlatformWidget);

        const exceptionDemoWidgets = await firstValueFrom(this.exceptionDemoWidgets());
        const filteredForRoles = platformWidgets.filter(
          widget =>
            exceptionDemoWidgets.some(exception => exception.cartridgeVariantId === widget.cartridgeId) ||
            widget.allowedRoles.some(role => roles.includes(role))
        );

        const filteredForQualificationLevel = filteredForRoles.filter(
          widget =>
            !widget.allowedQualificationLevels ||
            (organization.qualificationLevel &&
              widget.allowedQualificationLevels.includes(organization.qualificationLevel))
        );

        const filteredForValidationFunction: Widget[] = [];
        filteredForQualificationLevel.forEach(widget => {
          if (widget.validationFunction === undefined || widget.validationFunction(organization)) {
            filteredForValidationFunction.push(widget);
          }
        });

        const filteredWidgets = [...filteredForValidationFunction, ...nonPlatformWidgets];

        const notRemovableWidgets = this.platformWidgets['PLATFORM'].filter(
          widget =>
            exceptionDemoWidgets.some(exception => exception.cartridgeVariantId === widget.cartridgeId) ||
            (!widget.disabled &&
              widget.showInitial &&
              widget.isNotRemovable &&
              !filteredWidgets.map(filteredWidget => filteredWidget.id).includes(widget.id) &&
              (widget.validationFunction === undefined || widget.validationFunction(organization)) &&
              widget.allowedRoles.filter(role => roles.includes(role)).length > 0 &&
              (!widget.allowedQualificationLevels ||
                (organization.qualificationLevel &&
                  widget.allowedQualificationLevels.includes(organization.qualificationLevel))))
        );

        const uniqueWidgets = Array.from(
          new Map([...filteredWidgets, ...notRemovableWidgets].map(widget => [widget.id, widget])).values()
        );

        return uniqueWidgets.map(
          widget =>
            ({
              widgetId: widget.id,
              widgetType: widget.widgetType,
              gridPositionX: widget.gridPositionX,
              gridPositionY: widget.gridPositionY,
              cartridgeId: widget.cartridgeId,
            }) as WidgetPosition
        );
      }),
      switchMap(widgets => this.updateVisibleWidgets(widgets))
    );
  }

  exceptionDemoWidgets(): Observable<BookingData[]> {
    return this.bookingService
      .getBookingsForUser()
      .pipe(map(bookings => bookings.result.filter(booking => !!booking.usagePeriodEndDate)));
  }

  triggerWidgetRemoval({ id, cartridgeId }: WidgetIdentity): void {
    this.widgetRemovalTriggerSubject.next({ id, cartridgeId });
  }

  private async setVisibleWidgets(visibleWidgets: Widget[]): Promise<Widget[]> {
    if (visibleWidgets.length === 1 && visibleWidgets[0].id === 'initial') {
      const userRoles = await firstValueFrom(this.getUserRoles$());
      const exceptionDemoWidgets = await firstValueFrom(this.exceptionDemoWidgets());
      const filter = Array.from(
        new Map(
          this.platformWidgets['PLATFORM']
            .filter(
              platformWidget =>
                exceptionDemoWidgets.some(exception => exception.cartridgeVariantId === platformWidget.cartridgeId) ||
                (!platformWidget.disabled &&
                  platformWidget.showInitial &&
                  platformWidget.allowedRoles.some(role => userRoles.some(userRole => userRole === role)))
            )
            .map(platformWidget => [platformWidget.id, platformWidget])
        ).values()
      );

      return await firstValueFrom(
        this.updateVisibleWidgets(
          filter.map(widget => ({
            widgetId: widget.id,
            widgetType: widget.widgetType,
            cartridgeId: widget.cartridgeId ?? null,
          }))
        )
      );
    }

    return visibleWidgets.map(widget => {
      if (widget.widgetType === 'PLATFORM' || widget.widgetType === 'PLATFORM_CARTRIDGE') {
        return {
          ...widget,
          ...this.platformWidgets[widget.widgetType]
            .filter(widget => !widget.disabled)
            .find(platformWidget => platformWidget.id === widget.id),
        };
      }
      return widget;
    });
  }

  private filterInvalidPlatformWidgets(widget: Widget): boolean {
    return widget.id !== 'initial' && widget.widgetType === 'PLATFORM'
      ? this.platformWidgets[widget.widgetType]
          .filter(widget => !widget.disabled)
          .map(widget => widget.id)
          .includes(widget.id)
      : true;
  }

  private buildUsageReportWidget(cartridgeVariantId: string, price: Price): PlatformCartridgeWidget[] {
    if (!isBulkPrice(price)) {
      return [];
    }

    const [usageReportWidget] = this.platformWidgets['PLATFORM_CARTRIDGE'];

    return [
      {
        ...usageReportWidget,
        cartridgeId: cartridgeVariantId,
      },
    ];
  }
}
